import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import { useNavigate, useSearchParams } from "react-router-dom";
import IconComponent from "../../cubeComponent/icons";
import {
  getProjectContractPageUrl,
  getProjectDashboardPageUrl,
  getProjectEstimatesPageUrl,
  getProjectFilesPageUrl,
  getProjectAssetsPageUrl,
  getProjectGeneralClashPageUrl,
  getProjectIssuesPageUrl,
  getProjectMailboxPageUrl,
  getProjectReviewsPageUrl,
  getProjectSchedulesPageUrl,
  getProjectTasksPageUrl,
  getProjectTransmittalsPageUrl,
  getProjectSettingsPageUrl,
  getProjectWorkspacesListPageUrl,
  getProjectFormsPageUrl,
  getProjectRiskManagementListPageUrl,
} from "@Features/projects/utils/projectUrls";
import useStickySidebar from "./useStickySidebar";
import { getProjectObject } from "@Actions/projectSetting";
import { Box, Chip, Divider, Drawer, List, ListItem, ListItemIcon } from "@mui/material";

function Sidebar({ userData }: any) {
  const { layout, currentSelectedSidebarCell: activeCell } = useSelector((state: RootState) => state);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);

  const midIconsDivRef: any = useRef<any>(null);

  const { sideBarMidIcons, sideBarUpperFixedIcons, sideBarLowerFixedIcons }: any = useStickySidebar({
    layout,
    projectId,
    path: {
      Contract: (projectId: string) => getProjectContractPageUrl(projectId),
      Dashboard: (projectId: string) => getProjectDashboardPageUrl(projectId),
      Estimates: (projectId: string) => getProjectEstimatesPageUrl(projectId),
      Files: (projectId: string) => getProjectFilesPageUrl(projectId),
      Assets: (projectId: string) => getProjectAssetsPageUrl(projectId),
      GeneralClash: (projectId: string) => getProjectGeneralClashPageUrl(projectId),
      Issues: (projectId: string) => getProjectIssuesPageUrl(projectId),
      Mailbox: (projectId: string) => getProjectMailboxPageUrl(projectId),
      Reviews: (projectId: string) => getProjectReviewsPageUrl(projectId),
      Schedules: (projectId: string) => getProjectSchedulesPageUrl(projectId),
      Tasks: (projectId: string) => getProjectTasksPageUrl(projectId),
      Transmittals: (projectId: string) => getProjectTransmittalsPageUrl(projectId),
      Settings: (projectId: string) => getProjectSettingsPageUrl({ projectId, tab: "general_information" }),
      WorkspacesList: (projectId: string) => getProjectWorkspacesListPageUrl(projectId),
      RiskManagement: (projectId: string) => getProjectRiskManagementListPageUrl(projectId),
      Forms: (projectId: string, moduleName: string) => getProjectFormsPageUrl(projectId, moduleName),
    },
  });

  useEffect(() => {
    const sideBarState: any = localStorage.getItem("isSideBarExpanded");
    setIsExpanded(sideBarState);
  }, [sideBarMidIcons]);

  useEffect(() => {
    const el = midIconsDivRef.current;
    if (!el) return;

    const update = () => {
      const canScrollUp = el.scrollTop > 0;
      const canScrollDown = el.scrollHeight - el.clientHeight > el.scrollTop;

      setShowScrollUp(canScrollUp);
      setShowScrollDown(canScrollDown);
    };

    // 1. Observe layout / size changes
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    // 2. Observe scroll changes
    el.addEventListener("scroll", update, { passive: true });

    // 3. Initial check AFTER mount
    requestAnimationFrame(update);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, [midIconsDivRef, isExpanded]);

  const handleUpArrowClick = () => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: -100, behavior: "smooth" });
    }
  };

  const handleDownArrowClick = (customHeight = 100) => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: customHeight, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectObject({ id: projectId }));
    }
  }, [projectId]);

  const [showSettings, setShowSettings] = useState(true);

  useEffect(() => {
    if (layout.PROJECT_SETTINGS?.ENABLE === false) {
      setShowSettings(false);
    } else if (
      typeof layout.PROJECT_SETTINGS?.ENABLE === "object" &&
      layout.PROJECT_SETTINGS?.ENABLE?.CONDITION?.COMPANY_NAME
    ) {
      setShowSettings(userData?.company?.name == layout.PROJECT_SETTINGS?.ENABLE?.CONDITION?.COMPANY_NAME);
    }
  }, [layout, userData]);

  const handleSideBarExpandClick = () => {
    if (isExpanded) {
      localStorage.setItem("isSideBarExpanded", "");
      setIsExpanded(false);
    } else {
      localStorage.setItem("isSideBarExpanded", "yes");
      setIsExpanded(true);
    }
  };

  const handleRowClick = (path: any) => {
    navigate(path);
  };

  const getBetaTag = () => (
    <Chip
      size="small"
      sx={{
        fontWeight: 400,
        background: "#666a84",
        color: "white",
      }}
      label="BETA"
    />
  );
  const getIconsList = ({ iconsList, type, ref = undefined, css }: any) => {
    return (
      <List
        ref={ref}
        sx={{
          paddingBottom: "0.625rem",
          border: "none",
          display: "flex",
          paddingTop: 0,
          flexDirection: "column",
          gap: "0",
          ...css,
          opacity: type == "settings" && !showSettings ? 0.5 : 1,
          pointerEvents: type == "settings" && !showSettings && "none",
        }}
      >
        {iconsList.map(
          ({ allow, toolTip, path, name, svg, id, showBetaTag }: any) =>
            allow && (
              <ListItem key={id} disablePadding data-testid={name + "_SidebarIcons"}>
                <div className="flex justify-center py-4 items-center ps-2">
                  {activeCell === id ? (
                    <div
                      style={{ borderStartEndRadius: "0.375rem", borderEndEndRadius: "0.375rem" }}
                      className={`w-2 h-3 bg-[linear-gradient(#6bbbe3,_#3e8ab0)] absolute start-0`}
                    ></div>
                  ) : null}
                  <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconComponent
                      position="right"
                      onClick={() => handleRowClick(path)}
                      toolTipContent={toolTip}
                      showGradient={activeCell === id}
                      name={svg}
                      menuShow={isExpanded}
                      parentContainerClass={`flex gap-5 ${isExpanded ? "ps-4" : ""}`}
                    >
                      <div className="flex gap-3 items-center">
                        <span
                          className={`text-sm text-[var(--primary-text)] ${activeCell === id ? "text-primary_gradient_color1" : ""}`}
                        >
                          {name}
                        </span>
                        {showBetaTag && getBetaTag()}
                      </div>
                    </IconComponent>
                  </ListItemIcon>
                </div>
              </ListItem>
            ),
        )}
      </List>
    );
  };

  function getDrawer() {
    return (
      <Drawer
        sx={{
          width: isExpanded ? "250px" : "4.5rem",
          "& .MuiDrawer-paper": {
            width: isExpanded ? "250px" : "4.5rem",
            zIndex: 9,
            position: "fixed",
            paddingTop: "6.25rem",
            height: "100%",
            border: "none",
            display: "flex",
            overflow: "auto",
            backgroundColor: "#f0f5f9",
          },
        }}
        variant="permanent"
        open={isExpanded}
        anchor={"left"}
      >
        <div>
          {getIconsList({ iconsList: sideBarUpperFixedIcons, height: "28.125rem", css: {} })}
          <Divider />
        </div>
        <div className="flex overflow-auto flex-col h-full">
          {showScrollUp && (
            <Box
              onClick={handleUpArrowClick}
              id="upArrow"
              className={`flex cursor-pointer bg-surface_neutral p-3 justify-center items-center`}
            >
              <IconComponent name="BoldUpArrow" />
            </Box>
          )}
          {getIconsList({
            iconsList: sideBarMidIcons,
            type: "midIcons",
            ref: midIconsDivRef,
            css: { overflow: "hidden", height: "100%" },
          })}
          {showScrollDown && (
            <Box
              onClick={() => handleDownArrowClick()}
              className={`cursor-pointer flex bg-surface_neutral justify-center items-center p-3`}
            >
              <IconComponent name="BoldDownArrow" />
            </Box>
          )}
        </div>
        <div>
          <Divider />
          {getIconsList({ iconsList: sideBarLowerFixedIcons, type: "settings", css: { paddingTop: "0.875rem" } })}
        </div>
      </Drawer>
    );
  }

  return (
    <div
      className={`relative flex h-full bg-[#eff5f9] transition-[width] duration-300 ${
        localStorage.getItem("isSideBarExpanded") ? "w-[15.625rem]" : "w-20"
      }`}
    >
      {getDrawer()}
      <Box
        onClick={handleSideBarExpandClick}
        className={
          isExpanded
            ? "h-[var(--size-35)] cursor-pointer border-e-[length:var(--size-2)] border-e-[var(--text-light)] border-solid me-[13px] opacity-30 absolute top-1/2 bottom-1/2 ps-[var(--size-8)] z-10 end-[var(--size-2)] hover:cursor-pointer hover:border-none hover:bg-no-repeat hover:opacity-100 hover:bg-[url('@Assets/icons/svg/BigLeftArrow.svg')]"
            : "pe-[var(--size-8)] h-[var(--size-35)] cursor-pointer border-s-[length:var(--size-2)] border-s-[var(--text-light)] border-solid opacity-30 z-[11] top-1/2 absolute bottom-1/2 start-[var(--size-65)] hover:cursor-pointer hover:border-none hover:opacity-100 hover:bg-no-repeat hover:bg-[url('@Assets/icons/svg/BigRightArrow.svg')]"
        }
      />
    </div>
  );
}

export default Sidebar;
